/**
 * フォームの部品。
 *
 * react-hook-form は admin の依存に入っていないので、素の制御コンポーネントで書く。
 * 管理コンソールのフォームはどれも 10 項目未満で、バリデーションもサーバー側の
 * 封筒（`{error:{code,message,details}}`）を出せば足りるため、ライブラリを足さない。
 */
import * as React from 'react';
import { Label, cn } from '@mail-sv/ui';

/** ラベル + 補足 + エラーの定型。子要素へ `id` を自動で配る。 */
export function Field({
  label,
  required,
  hint,
  error,
  children,
  className,
}: {
  label: React.ReactNode;
  required?: boolean;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  const id = React.useId();
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {required ? (
          <span className="ml-1 text-destructive" aria-label="必須">
            *
          </span>
        ) : null}
      </Label>
      {React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement<{ id?: string }>, { id })
        : children}
      {error ? <p className="text-[13px] text-destructive">{error}</p> : null}
      {!error && hint ? <p className="text-[12px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/**
 * 素の `<select>`。Radix Select を使わないのは、
 * 「空値（未選択）を値として持てない」制約がフィルタ・フォーム双方で邪魔になるため。
 */
export function NativeSelect({
  value,
  onChange,
  options,
  id,
  disabled,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  id?: string;
  disabled?: boolean;
  className?: string;
}): React.ReactElement {
  return (
    <select
      id={id}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'h-10 w-full rounded-md border border-border bg-panel-elevated px-3 text-sm text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
        className,
      )}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
